import { localQueryClient } from '@app/icare/client/localQueryClient'
import { FindingsBadge, FindingsLabel, FindingsSelector } from '@app/icare/components/findings'
import { NotificationsDrawer } from '@app/icare/components/notification/NotificationsDrawer'
import { PatientDeceasedEditDrawer } from '@app/icare/entities/patient/edits/PatientDeceasedEditDrawer'
import {
  selectByFindingId,
  selectByFindingIds,
  selectByGroup,
  selectByGroups,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import ActiveEditors from '@app/icare/entities/user/components/ActiveEditors'
import {
  PatientReportModal,
  PatientReportOption,
  type PatientReportOptionType,
} from '@app/icare/modals/PatientReportModal'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { formatDate, mergeNonEmptyStrings } from '@pkg/shared/utils'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainerRuntime'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useRouter } from '@tanstack/react-router'
import { type AlertProps, Button, Divider, Dropdown, Space, Tour } from 'antd'
import { useEffect, useMemo, useRef } from 'react'
import { FaChevronDown, FaCross } from 'react-icons/fa'
import { FaFile, FaHandHoldingMedical, FaHouseMedicalCircleCheck, FaIdCard, FaPencil, FaPerson } from 'react-icons/fa6'

export const Route = createFileRoute('/_app/patienten/$id')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
  staticData: {
    layout: {
      route: {
        routes: [
          {
            path: '/patienten/$id',
            icon: <FaIdCard />,
            name: <Trans>Übersicht</Trans>,
          },
          {
            path: 'patient',
            icon: (
              <FindingsSelector
                selector={selectByGroups([
                  'personal',
                  'address',
                  'relatives',
                  'separateResidences',
                  'costCarriers',
                  'anamnesis',
                ])}
                children={f => <FindingsBadge dot tooltipPlacement="right" findings={f} children={<FaPerson />} />}
              />
            ),
            name: <Trans>Patient:in</Trans>,
            routes: [
              {
                path: '/patienten/$id/stammdaten',
                name: (
                  <FindingsLabel
                    label={t`Stammdaten`}
                    selector={selectByGroups(['personal', 'address', 'relatives'])}
                  />
                ),
              },
              {
                path: '/patienten/$id/aufenthaltsorte',
                name: (
                  <FindingsLabel
                    label={t`Gesonderte Aufenthaltsorte`}
                    selector={selectByGroups(['separateResidences'])}
                  />
                ),
              },
              {
                path: '/patienten/$id/kostentraeger',
                name: <FindingsLabel entity="costCarrier" selector={selectByGroups(['costCarriers'])} />,
              },
              {
                path: '/patienten/$id/anamnese',
                name: <FindingsLabel label={t`Anamnese`} selector={selectByGroups(['anamnesis'])} />,
              },
            ],
          },
          {
            path: 'documents',
            icon: (
              <FindingsSelector
                selector={selectByGroup('forms')}
                children={f => <FindingsBadge dot tooltipPlacement="right" findings={f} children={<FaFile />} />}
              />
            ),
            name: <Trans>Dokumente</Trans>,
            routes: [
              {
                path: '/patienten/$id/bedarfsermittlungen',
                name: <Trans>Bedarfsermittlungen</Trans>,
              },
              {
                path: '/patienten/$id/bia-messung',
                name: <Trans>BIA-Messungen</Trans>,
              },
              {
                path: '/patienten/$id/verlaufsdokumentation',
                name: <Trans>Verlaufsdokumentationen</Trans>,
              },
              {
                path: '/patienten/$id/stammblatt',
                name: (
                  <FindingsLabel
                    label={t`Stammblatt`}
                    selector={selectByFindingId('FormsPatientMasterDataSheetRequired')}
                  />
                ),
              },
              {
                path: '/patienten/$id/unterschriftsdokumente',
                name: (
                  <FindingsLabel
                    label={t`Unterschriftdokumente`}
                    selector={selectByFindingIds([
                      'FormsPatientInformationRequired',
                      'FormsPatientConsentAndConfidentialityRequired',
                      'FormsPatientSupplyRequired',
                    ])}
                  />
                ),
              },
            ],
          },
          {
            path: '/patienten/$id/therapien',
            icon: <FaHandHoldingMedical />,
            name: <FindingsLabel label={t`Therapien`} selector={selectByGroup('therapies')} />,
          },
          {
            path: 'versorgung',
            icon: (
              <FindingsSelector
                selector={selectByGroups([
                  'doctors',
                  'clinics',
                  'pharmacies',
                  'medicalTechnologies',
                  'careServices',
                  'cooperationPartners',
                  'adTeams',
                  'careManagers',
                  'applicants',
                ] as never)}
                children={f => (
                  <FindingsBadge dot tooltipPlacement="right" findings={f} children={<FaHouseMedicalCircleCheck />} />
                )}
              />
            ),
            name: <Trans>Versorgung</Trans>,
            routes: [
              {
                path: '/patienten/$id/praxen',
                name: <FindingsLabel entity="doctor" selector={selectByGroup('doctors')} />,
              },
              {
                path: '/patienten/$id/kliniken',
                name: <FindingsLabel entity="clinic" selector={selectByGroup('clinics')} />,
              },
              {
                path: '/patienten/$id/apotheken',
                name: <FindingsLabel entity="pharmacy" selector={selectByGroup('pharmacies')} />,
              },
              {
                path: '/patienten/$id/medizintechnikversorger',
                name: (
                  <FindingsLabel entity="medicalTechnology" selector={selectByGroup('medicalTechnologies' as never)} />
                ),
              },
              {
                path: '/patienten/$id/pflegedienste',
                name: <FindingsLabel entity="careService" selector={selectByGroup('careServices')} />,
              },
              {
                path: '/patienten/$id/kooperationspartner',
                name: (
                  <FindingsLabel entity="cooperationPartner" selector={selectByGroup('cooperationPartners' as never)} />
                ),
              },
              {
                path: '/patienten/$id/zustandigkeit',
                name: <FindingsLabel label={t`Zuständigkeit`} selector={selectByGroups(['adTeams', 'careManagers'])} />,
              },
              {
                path: '/patienten/$id/anmelder',
                name: <FindingsLabel label={t`Anmelder`} selector={selectByGroup('applicants')} />,
              },
            ],
          },
        ],
      },
    },
  },
  loader: () => ({
    breadcrumb: [
      {
        label: t`Patient:innen`,
        to: '/patienten',
      },
    ],
  }),
  head: ({ params }) => {
    const data = localQueryClient.getQueryData(patientQueryOptions(params.id).queryKey)
    if (!data) {
      return {}
    }
    return {
      meta: [
        {
          title: `iCare: ${mergeNonEmptyStrings([data?.personal?.firstName, data?.personal?.lastName, formatDate(data?.personal?.birthdate)], ' ')}`,
        },
      ],
    }
  },
})

function RouteComponent() {
  const { id } = Route.useParams()

  const router = useRouter()

  const { data, isFetched } = useSuspenseQuery(patientQueryOptions(id))

  // Workaround otherwise the querydata is not available in head
  useEffect(() => {
    if (isFetched) {
      void router.invalidate()
    }
  }, [isFetched, router])

  const headerActionsRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const editTour = useRouteTour('patient-edit', [
    {
      title: t`Aktionen`,
      description: t`Hier findest du Aktionen wie Meldungen und Berichte für die geöffnete Akte.`,
      target: headerActionsRef.current,
      placement: 'bottom',
    },
    {
      title: t`Inhalt`,
      description: t`Dies ist der Hauptinhalt der Akte. Hier bearbeitest du die Daten des Patienten.`,
      target: contentRef.current,
    },
  ])

  const alerts = useMemo(() => {
    const alerts: AlertProps[] = []

    if (data?.deletedAt) {
      alerts.push({
        type: 'error' as const,
        title: t`Eintrag ist gelöscht!`,
        showIcon: true,
      })
    }

    if (data?.deceased?.deceased) {
      alerts.push({
        type: 'error' as const,
        title: data?.deceased?.date
          ? t`Patient:in ist am ${formatDate(data?.deceased?.date)} verstorben.`
          : t`Patient:in ist verstorben.`,
        showIcon: true,
        icon: <FaCross />,
        action: (
          <Button
            type="text"
            icon={<FaPencil />}
            aria-label={t`Sterbemeldung bearbeiten`}
            // biome-ignore lint/style/noNonNullAssertion: data.id is guaranteed by the loader
            onClick={() => void PatientDeceasedEditDrawer.show({ patientId: data!.id! })}
          />
        ),
      })
    }

    return alerts
  }, [data])

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space ref={headerActionsRef} size="small" separator={<Divider vertical />}>
            <ActiveEditors
              routeId="/_app/patienten/$id"
              routeIds={['/_app/patienten/$id/_overview/']}
              params={{ id }}
            />
            <Space size="small">
              <Button
                size="small"
                loading={!data}
                shape="round"
                onClick={
                  /* biome-ignore lint/style/noNonNullAssertion: has loader that preloads data */
                  () => void NotificationsDrawer.show({ patientId: data!.id! })
                }
              >
                <Trans>Meldungen</Trans>
              </Button>
              <Dropdown
                menu={{
                  onClick: val =>
                    void PatientReportModal.show({
                      // biome-ignore lint/style/noNonNullAssertion: Has to be here
                      patientId: data!.id!,
                      report: val.key as PatientReportOptionType,
                    }),
                  items: [
                    { type: 'item', key: PatientReportOption.ANTIBIOSIS, label: t`Antibiose` },
                    {
                      type: 'item',
                      key: PatientReportOption.GENERAL,
                      label: t`allgemeiner Innendienstbericht`,
                    },
                    {
                      type: 'item',
                      key: PatientReportOption.INTERNAL,
                      label: t`Innendienstbericht für acriba`,
                    },
                    {
                      type: 'item',
                      key: PatientReportOption.PE_SUPPLY,
                      label: t`PE-Versorgungen`,
                    },
                  ],
                }}
              >
                <Button size="small" shape="round" icon={<FaChevronDown />}>{t`Berichte`}</Button>
              </Dropdown>
            </Space>
          </Space>
        ),
      },
      alerts,
    },
    [data?.id, alerts],
  )

  return (
    <>
      <div ref={contentRef}>
        <Outlet />
      </div>
      <Tour {...editTour} />
    </>
  )
}
